import { useEffect, useMemo, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, UsersRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function AdminGroups() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", color: "#0A195C", supervisor_ids: [], member_ids: [], allowed_campaigns: [] });
  const [saving, setSaving] = useState(false);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try {
      const [g, u, c] = await Promise.all([
        api.get("/admin/groups"),
        api.get("/admin/users"),
        api.get("/admin/resources/campaigns").catch(() => ({ data: [] })),
      ]);
      setGroups(g.data || []); setUsers(u.data || []); setCampaigns(c.data || []);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startNew() { setForm({ name: "", description: "", color: "#0A195C", supervisor_ids: [], member_ids: [], allowed_campaigns: [] }); setEditing("new"); }
  function startEdit(g) {
    setForm({
      name: g.name,
      description: g.description || "",
      color: g.color || "#0A195C",
      supervisor_ids: g.supervisor_ids || [],
      member_ids: g.member_ids || [],
      allowed_campaigns: g.allowed_campaigns || [],
    });
    setEditing(g);
  }
  async function save() {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/groups", form);
      else await api.patch(`/admin/groups/${editing.id}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(g) {
    if (!confirm(`Delete group "${g.name}"? Members will keep their accounts.`)) return;
    try { await api.delete(`/admin/groups/${g.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }
  const supervisors = useMemo(() => users.filter((u) => ["supervisor","admin","super_admin"].includes(u.role)), [users]);
  const members     = useMemo(() => users.filter((u) => u.status !== "suspended"), [users]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="admin-groups-title">User Groups (Teams)</h1>
          <p className="text-sm text-muted-foreground mt-1">Group agents into teams. Used for routing, reporting, and supervisor scoping.</p>
        </div>
        {canWrite && <Button data-testid="add-group-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
          <Plus className="w-4 h-4" /> New group
        </Button>}
      </div>
      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
                <th className="px-4 py-2">Group</th><th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">Supervisors</th><th className="px-4 py-2">Members</th>
                <th className="px-4 py-2">Campaigns</th>
                <th className="px-4 py-2 w-24">Actions</th>
              </tr></thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-group-${g.id}`}>
                    <td className="px-4 py-2.5"><div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: g.color }} /><span className="font-medium">{g.name}</span></div></td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{g.description || "—"}</td>
                    <td className="px-4 py-2.5"><Badge variant="outline">{(g.supervisor_ids||[]).length}</Badge></td>
                    <td className="px-4 py-2.5"><Badge variant="outline">{(g.member_ids||[]).length}</Badge></td>
                    <td className="px-4 py-2.5"><Badge variant="outline" data-testid={`row-group-camps-${g.id}`}>{(g.allowed_campaigns||[]).length}</Badge></td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        {canWrite && <Button data-testid={`edit-group-${g.id}`} onClick={()=>startEdit(g)} variant="ghost" size="icon" className="h-8 w-8"><Edit2 className="w-4 h-4" /></Button>}
                        {canDelete && <Button data-testid={`delete-group-${g.id}`} onClick={()=>remove(g)} variant="ghost" size="icon" className="h-8 w-8 text-destructive"><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {groups.length === 0 && <tr><td colSpan={6} className="text-center py-12"><UsersRound className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" /><div className="text-sm text-muted-foreground">No groups yet.</div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing==="new"?"New Group":`Edit ${editing?.name}`}</DialogTitle><DialogDescription>Manage supervisors and members of this team.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="group-name-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
              <div><Label>Color</Label><Input data-testid="group-color-input" value={form.color} onChange={(e)=>setForm({...form, color:e.target.value})} /></div>
              <div className="sm:col-span-2"><Label>Description</Label><Input data-testid="group-description-input" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} /></div>
            </div>
            <div>
              <Label>Supervisors</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {supervisors.map((u) => {
                  const on = (form.supervisor_ids||[]).includes(u.id);
                  return <button key={u.id} type="button" data-testid={`sup-${u.id}`} onClick={()=>{const cur=form.supervisor_ids||[];setForm({...form,supervisor_ids:on?cur.filter(x=>x!==u.id):[...cur,u.id]});}} className={`px-2.5 py-1 rounded-full text-xs border ${on?"bg-secondary text-secondary-foreground border-secondary":"bg-card border-border hover:bg-muted"}`}>{u.name}</button>;
                })}
              </div>
            </div>
            <div>
              <Label>Members</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-60 overflow-y-auto">
                {members.map((u) => {
                  const on = (form.member_ids||[]).includes(u.id);
                  return <button key={u.id} type="button" data-testid={`mem-${u.id}`} onClick={()=>{const cur=form.member_ids||[];setForm({...form,member_ids:on?cur.filter(x=>x!==u.id):[...cur,u.id]});}} className={`px-2.5 py-1 rounded-full text-xs border ${on?"bg-secondary text-secondary-foreground border-secondary":"bg-card border-border hover:bg-muted"}`}>{u.name}</button>;
                })}
              </div>
            </div>
            <div>
              <Label>Allowed Campaigns</Label>
              <p className="text-[11px] text-muted-foreground -mt-1 mb-1.5">Members of this group can join these campaigns from the workspace topbar.</p>
              <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-60 overflow-y-auto">
                {campaigns.length === 0 ? (
                  <span className="text-xs text-muted-foreground italic">No campaigns defined yet — create one under Outbound → Campaigns first.</span>
                ) : campaigns.map((c) => {
                  const on = (form.allowed_campaigns||[]).includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`grp-camp-${c.id}`}
                      onClick={() => {
                        const cur = form.allowed_campaigns || [];
                        setForm({ ...form, allowed_campaigns: on ? cur.filter(x => x !== c.id) : [...cur, c.id] });
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
                    >
                      {c.name || c.label || c.id}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="group-save-btn" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
